import { api } from "./client";
import type { LoginResponse, User } from "./types";
import type { ActivateAccountRequest, ActivateAccountResponse } from "@mycompbox/shared";

export const authApi = {
  login: (email: string, password: string) =>
    api.post<LoginResponse>("/auth/login", { email, password }),

  refresh: (refreshToken: string) =>
    api.post<LoginResponse>("/auth/refresh", { refreshToken }),

  me: () => api.get<User>("/auth/me"),

  logout: () => api.post("/auth/logout", {}),

  // Invitation activation. (The legacy /auth/signup route stays a 410
  // tombstone; self-serve signup uses /auth/register below.)
  activate: (data: ActivateAccountRequest): Promise<ActivateAccountResponse> =>
    api.post<ActivateAccountResponse>("/auth/activate", data),

  /**
   * Self-serve signup: creates a new organization with the signer as its
   * admin and emails a verification link. Returns no tokens — the account can
   * sign in only after the link is followed.
   */
  register: (data: RegisterRequest) =>
    api.post<RegisterResponse>("/auth/register", data),

  /** Consume an emailed verification token; signs the user in. */
  verifyEmail: (token: string) =>
    api.post<LoginResponse>("/auth/verify-email", { token }),

  /** Re-send the verification email (always succeeds — no account enumeration). */
  resendVerification: (email: string) =>
    api.post<{ message: string }>("/auth/resend-verification", { email }),

  /** Which external sign-in providers are configured on the server. */
  providers: () => api.get<{ google: boolean; microsoft: boolean }>("/auth/providers"),

  /** Provider authorization URL for a PKCE challenge, plus the signed state. */
  oauthUrl: (provider: OAuthProvider, codeChallenge: string) =>
    api.post<{ url: string; state: string }>(`/auth/${provider}/url`, { codeChallenge }),

  /**
   * Complete external sign-in. A first-time user joins the organization that
   * verified their company domain, or else gets a new organization.
   */
  oauthCallback: (
    provider: OAuthProvider,
    data: { code: string; state: string; codeVerifier: string }
  ) =>
    api.post<LoginResponse & { isNewUser: boolean; joinedExisting: boolean }>(
      `/auth/${provider}/callback`,
      data
    ),
};

/** External identity providers ("Sign in with Google / Microsoft"). */
export type OAuthProvider = "google" | "microsoft";

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  organizationName?: string;
}

export interface RegisterResponse {
  message: string;
  email: string;
  verificationRequired: true;
}
